import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Form, ResponsiveContext } from 'grommet';
import { List } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import {
  ApiError,
  ClientService,
  EquipmentCategoryService,
  FSERegistrationStatusService,
  FacilityService,
} from '/src/api';
import { ChargingNetworkService } from '/src/api/services/ChargingNetworkService';
import { ChargingTypeService } from '/src/api/services/ChargingTypeService';
import { EquipmentUseCaseService } from '/src/api/services/EquipmentUseCaseService';
import { ForkliftClassificationService } from '/src/api/services/ForkliftClassificationService';
import {
  Box,
  BreadcrumbNav,
  BreadcrumbNavProps,
  FirstActivePeriodSelect,
  FormCard,
  FormCardSection,
  FormPage,
  Input,
  Line,
  Select,
} from '/src/components';
import { config } from '/src/config';
import { useEquipmentStore, useGlobalStore, useUserStore } from '/src/context';
import {
  EquipmentCategory,
  FSERegistrationStatus,
  Facility,
  TFacilityId,
  TProgramId,
  hiddenEquipmentTypes,
  isFciCategory,
} from '/src/lib/models';
import { ChargingNetworks } from '/src/lib/models/ChargingNetwork';
import { ChargingType } from '/src/lib/models/ChargingType';
import { EquipmentUseCase } from '/src/lib/models/EquipmentUseCase';
import { ForkliftClassification } from '/src/lib/models/ForkliftClassification';
import { toastMessages } from '/src/lib/toast';
import { CountryId, DateString, SelectOptions, TResponseMetadata } from '/src/lib/types';
import {
  facilityIsOrWa,
  getFacilityLabel,
  getFormattedDateString,
  getIsMobile,
  getPageTitle,
  getQueryParams,
} from '/src/utils';

export const CreateEquipmentPage = observer(() => {
  /** Context **/
  const navigate = useNavigate();
  const globalStore = useGlobalStore();
  const equipmentStore = useEquipmentStore();
  const userStore = useUserStore();
  const screenSize = useContext(ResponsiveContext);
  const isMobile = getIsMobile(screenSize);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  /** Params **/
  const queryParams = getQueryParams(location.search);
  const categoryName = queryParams.category?.replace(/%20/g, ' ') ?? '';
  const facilityId = parseInt(queryParams.facility_id ?? '');
  const params = useParams();
  const clientId = parseInt(params.client_id ?? '');

  /** State **/
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [, setIsLoadingCategories] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<Facility>();
  const [currentFacilityPage, setCurrentFacilityPage] = useState(1);
  const [isUserLocation, setIsUserLocation] = useState(true);

  const [categories, setCategories] = useState<EquipmentCategory[]>();
  const [fseRegistrationStatuses, setFSERegistrationStatuses] = useState<FSERegistrationStatus[]>();
  const [chargingNetworks, setChargingNetworks] = useState<ChargingNetworks[]>();
  const [forkliftClassification, setforkliftClassification] = useState<ForkliftClassification[]>();
  const [equipmentUseCases, setEquipmentUseCases] = useState<EquipmentUseCase[]>();
  const [chargingTypes, setChargingTypes] = useState<ChargingType[]>();
  const [clientName, setClientName] = useState('');
  const [isCanada, setIsCanada] = useState(false);

  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [facilityOptions, setFacilityOptions] = useState<SelectOptions<TFacilityId>>([]);
  const [facilityMetadata, setFacilityMetadata] = useState<TResponseMetadata>();
  const [breadcrumbNavItems, setBreadcrumbNavItems] = useState<BreadcrumbNavProps['previousPages']>();

  /** Computed **/
  const programId = selectedFacility?.program?.id;
  const isOrWa = facilityIsOrWa(selectedFacility);
  const isForklift = !!(equipmentStore.formValues.category_name ?? '').toLowerCase().includes('forklift');
  const isApproved = equipmentStore.formValues.fse_registration_status_id === 3;
  const isPending =
    !equipmentStore.formValues.fse_registration_status_id || equipmentStore.formValues.fse_registration_status_id === 1;

  const firstDayInServiceRange = useMemo(() => {
    if (!selectedFacility || !equipmentStore.formValues.first_active_reporting_quarter) return;
    const start = dayjs(selectedFacility.first_active_reporting_quarter).toISOString();
    const end = dayjs(equipmentStore.formValues.first_active_reporting_quarter)
      .add(2, 'months')
      .endOf('month')
      .toISOString();

    return [start, end] as [start: DateString, end: DateString];
  }, [selectedFacility, equipmentStore.formValues.first_active_reporting_quarter]);

  const equipmentTypeOptions =
    categories
      ?.find(({ name }) => name === equipmentStore.formValues.category_name)
      ?.equipment_types?.filter((type) => !hiddenEquipmentTypes.has(type.id))
      ?.map((type) => ({
        label: type.name,
        value: type.id,
      })) ?? [];

  /** Methods **/
  const fetchPageData = async () => {
    try {
      const [
        { data: facilitiesData, meta: facilitiesMeta },
        registrationStatuses,
        chargingNetworks,
        forkliftClassification,
        equipmentUseCases,
        chargingTypes,
      ] = await Promise.all([
        FacilityService.list({ client_id: clientId, page: currentFacilityPage, limit: 1000 }),
        FSERegistrationStatusService.listFSERegistrationStatuses(),
        ChargingNetworkService.listChargingNetworks(),
        ForkliftClassificationService.listForkliftClassifications(),
        EquipmentUseCaseService.listEquipmentUseCases(),
        ChargingTypeService.listChargingTypes(),
      ]);

      const client =
        userStore.isClientUser || userStore.isFacilityUser
          ? userStore.user?.clients.find((client) => client.id === clientId)
          : await ClientService.get({ id: clientId });

      const clientIsCanada = client?.country_id === CountryId.Canada;
      if (client) {
        setClientName(client.name);
        setIsCanada(clientIsCanada);
      }

      setFacilities(facilitiesData);
      setFacilityMetadata(facilitiesMeta);
      setFacilityOptions([
        ...facilityOptions,
        ...facilitiesData.map((facility) => ({ label: getFacilityLabel(facility, true), value: facility.id })),
      ]);
      const facility = facilityId ? facilitiesData.find((facility) => facility.id === facilityId) : selectedFacility;
      setSelectedFacility(facility);
      fetchNavItems(client?.name ?? '', facility);
      const programId = facility?.program?.id;
      setFSERegistrationStatuses(registrationStatuses);
      setChargingNetworks(chargingNetworks);
      setforkliftClassification(forkliftClassification);
      setEquipmentUseCases(equipmentUseCases);
      setChargingTypes(chargingTypes);

      let category: EquipmentCategory | undefined;
      if (programId) {
        const categories = await EquipmentCategoryService.listEquipmentCategories({ program_id: programId });
        setCategories(categories);
        category = categories?.find((category) => category.name === categoryName);
      }

      equipmentStore.setFormValues({
        ...equipmentStore.formValues,
        facility_id: facilityId,
        category_name: categoryName,
        equipment_category_id: category?.id ?? NaN,
        is_metered: clientIsCanada ? true : equipmentStore.formValues.is_metered,
      });
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.generic.error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async (programId: TProgramId) => {
    setIsLoadingCategories(true);
    try {
      const categories = await EquipmentCategoryService.listEquipmentCategories({ program_id: programId });
      setCategories(categories);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.generic.error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const fetchNavItems = (clientName: string, facility?: Facility) => {
    const navItems: BreadcrumbNavProps['previousPages'] = [
      { name: 'Clients', link: !userStore.isClientUser && !userStore.isFacilityUser ? '/clients' : undefined },
      { name: clientName, link: !userStore.isFacilityUser ? `/clients/${clientId}` : undefined },
    ];
    if (facility) {
      navItems.push({ name: 'Facilities', link: `/clients/${clientId}/facilities` });
      navItems.push({
        name: getFacilityLabel(facility),
        link: `/clients/${clientId}/facilities/${facility.id}`,
      });
      navItems.push({ name: 'Equipment', link: `/clients/${clientId}/facilities/${facility.id}/equipment` });
    } else {
      navItems.push({ name: 'Equipment' });
    }
    setBreadcrumbNavItems(navItems);
  };

  const nextFacilityPage = async () => {
    if (isLoadingFacilities || (facilityMetadata && currentFacilityPage + 1 > facilityMetadata.last_page)) return;
    setIsLoadingFacilities(true);
    try {
      setCurrentFacilityPage(currentFacilityPage + 1);
      const { meta, data } = await FacilityService.list({
        client_id: clientId,
        page: currentFacilityPage + 1,
      });
      setFacilities([...facilities, ...data]);
      setFacilityOptions([
        ...facilityOptions,
        ...data.map((facility) => ({ label: getFacilityLabel(facility), value: facility.id })),
      ]);
      setFacilityMetadata(meta);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.fetchEquipment.error);
    } finally {
      setIsLoadingFacilities(false);
    }
  };

  const createEquipment = async () => {
    if (!selectedFacility) {
      toast.error(toastMessages.formValidation.requiredFields);
      return;
    }

    try {
      setIsCreating(true);
      equipmentStore.setFormErrors({});

      const equipment = await (userStore.isInternalUser
        ? equipmentStore.createEquipmentInternal(selectedFacility)
        : equipmentStore.createEquipmentExternal(selectedFacility));

      if (!equipment) {
        if (Object.keys(equipmentStore.formErrors).length > 0) {
          toast.error(toastMessages.formValidation.requiredFields);
        }
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      toast.success(toastMessages.createEquipment.success);
      navigate(`/clients/${clientId}/equipment/${equipment.id}`);
    } catch (err) {
      const error = err as ApiError;
      let showToast = true;
      if (error.status === 400) {
        const body = (error.body ?? {}) as Record<string, string>;
        if (body?.message?.toLowerCase().includes('first active reporting quarter')) {
          equipmentStore.formErrors['first_active_reporting_quarter'] = body?.message;
          showToast = false;
        }
      }
      if (showToast) globalStore.handleApiError(error, toastMessages.createEquipment.error);
    } finally {
      setIsCreating(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    document.title = getPageTitle(config.client.basePageTitle, 'Add Equipment');
    equipmentStore.resetForm();
  }, []);

  useEffect(() => {
    if (isLoading) {
      fetchPageData();
    }
  }, [isLoading]);

  useEffect(() => {
    if (programId && equipmentStore.formValues.category_name) fetchCategories(programId);
  }, [programId, equipmentStore.formValues.category_name]);

  // Auto-fill lat/long (if applicable)
  useEffect(() => {
    const latLongIsEmpty = !equipmentStore.formValues.latitude && !equipmentStore.formValues.longitude;
    if (
      isForklift &&
      (latLongIsEmpty || !isUserLocation) &&
      selectedFacility?.address_latitude &&
      selectedFacility?.address_longitude
    ) {
      equipmentStore.updateFormValue('latitude', selectedFacility.address_latitude);
      equipmentStore.updateFormValue('longitude', selectedFacility.address_longitude);
      setIsUserLocation(false);
    }
  }, [
    isForklift,
    equipmentStore.formValues.latitude,
    equipmentStore.formValues.longitude,
    isUserLocation,
    selectedFacility?.address_latitude,
    selectedFacility?.address_longitude,
  ]);

  // Change Category
  useEffect(() => {
    if (!categories) return;

    const category = categories.find(({ name }) => name === equipmentStore.formValues.category_name);
    const newCategoryId = category?.id;
    equipmentStore.updateFormValue('equipment_category_id', newCategoryId);

    const equipmentTypes =
      category?.equipment_types?.filter(
        (type) => !hiddenEquipmentTypes.has(type.id) || type.id === equipmentStore.formValues.equipment_type_id
      ) || [];
    const validTypeIds = equipmentTypes.map((type) => type.id);

    if (equipmentTypes.length === 1) {
      equipmentStore.updateFormValue('equipment_type_id', equipmentTypes[0].id);
    } else if (!validTypeIds.includes(equipmentStore.formValues.equipment_type_id as number)) {
      equipmentStore.updateFormValue('equipment_type_id', undefined);
    }

    if (!isFciCategory(newCategoryId)) {
      equipmentStore.updateFormValue('nameplate_rating_kw', null);
    }

    if (newCategoryId !== 5) {
      equipmentStore.updateFormValue('forklift_classification_id', null);
    }
  }, [categories, equipmentStore.formValues?.category_name]);

  // Change Facility
  useEffect(() => {
    if (equipmentStore.formValues.facility_id && selectedFacility?.id !== equipmentStore.formValues.facility_id) {
      const facility = facilities.find((facility) => facility.id === equipmentStore.formValues.facility_id);
      setSelectedFacility(facility);
      fetchNavItems(clientName, facility);
    }
  }, [equipmentStore.formValues.facility_id, selectedFacility, facilities, clientName]);

  /** Render **/
  return (
    <FormPage
      title={`${t('equipment:add')} ${
        equipmentStore.formValues.category_name
          ? equipmentStore.formValues.category_name === 'Electric Forklift'
            ? 'Electric Forklift'
            : `${equipmentStore.formValues.category_name} Charger`
          : 'Equipment'
      }`}
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={breadcrumbNavItems ?? []}
          currentPageName={`${t('equipment:add')} ${
            equipmentStore.formValues.category_name || t('equipment:equipment')
          }`}
        />
      }
      isLoading={isLoading}
    >
      <FormCard
        title={t('equipment:equipmentDetails')}
        icon={<List size="24px" color="brand" />}
        isLoading={isLoading || isCreating}
        onSubmit={createEquipment}
      >
        <Form>
          <Box direction={isMobile ? 'column' : 'row'} margin={{ bottom: '1rem' }} gap="1rem">
            <Box width={isMobile ? '100%' : '60%'} gap="1rem">
              <FormCardSection row>
                <Select
                  id="category_name"
                  label={t('equipment:equipmentCategory')}
                  placeholder="Choose..."
                  value={equipmentStore.formValues.category_name}
                  setValue={(value) => equipmentStore.updateFormValue('category_name', value)}
                  options={categories?.map((category) => category.name) ?? []}
                  error={equipmentStore.formErrors['category_name']}
                  emptySearchMessage="No Facility selected."
                  required
                  flex
                />
                <Select
                  id="equipment_type_id"
                  label={t('equipment:equipmentType')}
                  placeholder="Choose..."
                  emptySearchMessage="No Equipment Category selected."
                  value={equipmentStore.formValues.equipment_type_id}
                  setValue={(value) => equipmentStore.updateFormValue('equipment_type_id', value)}
                  options={equipmentTypeOptions}
                  flex
                  error={equipmentStore.formErrors['equipment_type_id']}
                  required
                />
                {selectedFacility?.program?.id === 1 && equipmentStore.formValues.equipment_category_id === 5 && (
                  <Select
                    id="forklift_classification"
                    label={t('equipment:forkliftClassification')}
                    placeholder="Choose..."
                    value={equipmentStore.formValues.forklift_classification_id}
                    setValue={(value) => equipmentStore.updateFormValue('forklift_classification_id', value)}
                    options={(forkliftClassification ?? []).map((s) => ({ label: s.name, value: s.id }))}
                    error={equipmentStore.formErrors['forklift_classification_id']}
                    required={
                      selectedFacility?.program?.id === 1 && equipmentStore.formValues.equipment_category_id === 5
                    }
                    disabled={isCanada}
                    flex
                  />
                )}
                {isFciCategory(equipmentStore.formValues.equipment_category_id) && (
                  <Input
                    id="nameplate_rating_kw"
                    label={t('equipment:nameplateRating')}
                    value={equipmentStore.formValues.nameplate_rating_kw ?? ''}
                    setValue={(value) => equipmentStore.updateFormValue('nameplate_rating_kw', value)}
                    error={equipmentStore.formErrors['nameplate_rating_kw']}
                    onSubmit={createEquipment}
                    flex
                    disabled={isCanada}
                    required
                  />
                )}
              </FormCardSection>
              <Line margin="0.5rem" />
              <FormCardSection>
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                  <Input
                    id="serial_number"
                    label={t('equipment:serialNumber')}
                    value={equipmentStore.formValues.serial_number}
                    setValue={(value) => equipmentStore.updateFormValue('serial_number', value)}
                    error={equipmentStore.formErrors['serial_number']}
                    onSubmit={createEquipment}
                    flex
                    required
                  />
                  <Input
                    id="unit_number"
                    label={t('equipment:unitNumber')}
                    value={equipmentStore.formValues.unit_number ?? ''}
                    setValue={(value) => equipmentStore.updateFormValue('unit_number', value)}
                    error={equipmentStore.formErrors['unit_number']}
                    onSubmit={createEquipment}
                    flex
                  />
                </Box>
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem" margin={{ top: 'small' }}>
                  <Input
                    id="model_year"
                    label={t('equipment:modelYear')}
                    value={equipmentStore.formValues.model_year}
                    setValue={(value) => equipmentStore.updateFormValue('model_year', value)}
                    error={equipmentStore.formErrors['model_year']}
                    onSubmit={createEquipment}
                    flex
                    type="number"
                    required
                  />
                  <Input
                    id="manufacturer"
                    label={t('equipment:manufacturer')}
                    value={equipmentStore.formValues.manufacturer}
                    setValue={(value) => equipmentStore.updateFormValue('manufacturer', value)}
                    error={equipmentStore.formErrors['manufacturer']}
                    onSubmit={createEquipment}
                    flex
                    required
                  />
                  <Input
                    id="model_number"
                    label={t('equipment:modelNumber')}
                    value={equipmentStore.formValues.model_number}
                    setValue={(value) => equipmentStore.updateFormValue('model_number', value)}
                    error={equipmentStore.formErrors['model_number']}
                    onSubmit={createEquipment}
                    flex
                  />
                </Box>
              </FormCardSection>
              <Line margin="xsmall" />
              <FormCardSection>
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                  <Select
                    id="is_metered"
                    label={t('equipment:metered')}
                    value={equipmentStore.formValues.is_metered ? 'Yes' : 'No'}
                    setValue={(value) => equipmentStore.updateFormValue('is_metered', value === 'Yes')}
                    options={['Yes', 'No']}
                    error={equipmentStore.formErrors['is_metered']}
                    required
                    disabled={isCanada}
                    flex
                  />
                  {!isOrWa && equipmentStore.formValues.is_metered && (
                    <Select
                      id="charging_network"
                      label={t('equipment:chargingNetwork')}
                      placeholder="Choose..."
                      value={equipmentStore.formValues.charging_network_id}
                      setValue={(value) => equipmentStore.updateFormValue('charging_network_id', value)}
                      options={(chargingNetworks ?? []).map((s) => ({ label: s.name, value: s.id }))}
                      error={equipmentStore.formErrors['charging_network_id']}
                      required
                      flex
                    />
                  )}
                  <FirstActivePeriodSelect
                    id="first_active_reporting_quarter"
                    label={t('equipment:firstActiveReportingPeriod')}
                    placeholder="Select Period..."
                    value={equipmentStore.formValues.first_active_reporting_quarter ?? ''}
                    setValue={(value) => equipmentStore.updateFormValue('first_active_reporting_quarter', value)}
                    clientId={selectedFacility?.client_id}
                    reportingPeriodTypeId={selectedFacility?.reporting_period_type_id}
                    firstActivePeriod={selectedFacility?.first_active_reporting_quarter}
                    programId={selectedFacility?.program?.id}
                    error={equipmentStore.formErrors['first_active_reporting_quarter']}
                    required
                  />
                </Box>

                {isOrWa && (
                  <Box direction={isMobile ? 'column' : 'row'} gap="1rem" margin={{ top: 'small' }}>
                    {equipmentStore.formValues.is_metered && (
                      <Select
                        id="charging_network"
                        label={t('equipment:chargingNetwork')}
                        placeholder="Choose..."
                        value={equipmentStore.formValues.charging_network_id}
                        setValue={(value) => equipmentStore.updateFormValue('charging_network_id', value)}
                        options={(chargingNetworks ?? []).map((s) => ({ label: s.name, value: s.id }))}
                        error={equipmentStore.formErrors['charging_network_id']}
                        required
                        flex
                      />
                    )}
                    <Input
                      animation={{ type: 'slideRight' }}
                      id="first_day_in_service"
                      label={t('equipment:firstDayInService')}
                      flex
                      value={equipmentStore.formValues.first_day_in_service ?? ''}
                      displayValue={getFormattedDateString(
                        equipmentStore.formValues.first_day_in_service ?? '',
                        isCanada
                      )}
                      dateBounds={firstDayInServiceRange}
                      setValue={(value) => equipmentStore.updateFormValue('first_day_in_service', value)}
                      error={equipmentStore.formErrors['first_day_in_service']}
                      onSubmit={createEquipment}
                      componentType="date"
                      required
                      disabled={!equipmentStore.formValues.first_active_reporting_quarter}
                      placeholder={
                        !equipmentStore.formValues.first_active_reporting_quarter
                          ? 'Select a First Reporting Period'
                          : undefined
                      }
                    />
                  </Box>
                )}

                {(selectedFacility?.program?.id === 4 || selectedFacility?.program?.id === 5) && (
                  <Box direction={isMobile ? 'column' : 'row'} gap="1rem" margin={{ top: 'small' }}>
                    <Select
                      id="equipment_use_case"
                      label={t('equipment:equipmentUseCase')}
                      placeholder="Choose..."
                      value={equipmentStore.formValues.equipment_use_case_id}
                      setValue={(value) => equipmentStore.updateFormValue('equipment_use_case_id', value)}
                      options={(equipmentUseCases ?? []).map((s) => ({ label: s.name, value: s.id }))}
                      error={equipmentStore.formErrors['equipment_use_case_id']}
                      required
                      flex
                    />
                    <Select
                      id="charging_type"
                      label={t('equipment:chargingType')}
                      placeholder="Choose..."
                      value={equipmentStore.formValues.charging_type_id}
                      setValue={(value) => equipmentStore.updateFormValue('charging_type_id', value)}
                      options={(chargingTypes ?? []).map((s) => ({ label: s.name, value: s.id }))}
                      error={equipmentStore.formErrors['charging_type_id']}
                      required
                      flex
                    />
                  </Box>
                )}
              </FormCardSection>
              <Line margin="xsmall" />
              <Box flex="grow">
                <Input
                  id="comments"
                  label={t('common:comments')}
                  value={equipmentStore.formValues.comments ?? ''}
                  setValue={(value) => equipmentStore.updateFormValue('comments', value)}
                  error={equipmentStore.formErrors['comments']}
                  onSubmit={createEquipment}
                  height={userStore.isExternalUser ? '12rem' : '14rem'}
                  componentType="textArea"
                  centerLabel={false}
                />
              </Box>
            </Box>
            {!isMobile && <Line direction="vertical" margin="3rem" />}
            <Box gap="1rem" width={isMobile ? '100%' : '40%'}>
              {!isMobile && (
                <FormCardSection>
                  <Select
                    id="facility_id"
                    label={t('common:facility')}
                    value={equipmentStore.formValues.facility_id}
                    setValue={(value) => equipmentStore.updateFormValue('facility_id', value)}
                    onMore={nextFacilityPage}
                    options={facilityOptions}
                    error={equipmentStore.formErrors['facility_id']}
                    required
                  />
                </FormCardSection>
              )}
              {!isMobile && userStore.isInternalUser && <Line margin="0.5rem" />}
              {userStore.isInternalUser && (
                <>
                  {isMobile && <Line margin="0.5rem" />}
                  <FormCardSection title="FSE">
                    <Box gap="1rem">
                      <Select
                        id="fse_registration_status_id"
                        label={t('equipment:fseRegistrationStatus')}
                        placeholder="Choose..."
                        value={equipmentStore.formValues.fse_registration_status_id}
                        setValue={(value) => equipmentStore.updateFormValue('fse_registration_status_id', value)}
                        options={(fseRegistrationStatuses ?? []).map((s) => ({ label: s.name, value: s.id }))}
                        error={equipmentStore.formErrors['fse_registration_status_id']}
                        required
                      />

                      <Input
                        id="fse_id"
                        label="FSE ID"
                        value={equipmentStore.formValues.fse_id ?? ''}
                        setValue={(value) => equipmentStore.updateFormValue('fse_id', value)}
                        error={equipmentStore.formErrors['fse_id']}
                        onSubmit={createEquipment}
                        disabled={!isApproved}
                        required={equipmentStore.isApproved}
                      />

                      <Input
                        id="fse_ru"
                        label="FSE RU"
                        value={equipmentStore.formValues.fse_ru ?? ''}
                        setValue={(value) => equipmentStore.updateFormValue('fse_ru', value)}
                        error={equipmentStore.formErrors['fse_ru']}
                        disabled={isPending}
                        onSubmit={createEquipment}
                      />
                    </Box>
                  </FormCardSection>
                </>
              )}
              <Line margin="0.5rem" />
              <FormCardSection title={t('equipment:location')}>
                {/* Mobile view Facility moved under Location */}
                {isMobile && (
                  <FormCardSection>
                    <Select
                      id="facility_id"
                      label={t('common:facility')}
                      value={equipmentStore.formValues.facility_id}
                      setValue={(value) => equipmentStore.updateFormValue('facility_id', value)}
                      onMore={nextFacilityPage}
                      options={facilityOptions}
                      error={equipmentStore.formErrors['facility_id']}
                      required
                    />
                  </FormCardSection>
                )}
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                  <Input
                    id="latitude"
                    label={t('equipment:latitude')}
                    value={equipmentStore.formValues.latitude}
                    type="number"
                    setValue={(value) => {
                      equipmentStore.updateFormValue('latitude', value);
                      setIsUserLocation(true);
                    }}
                    error={equipmentStore.formErrors['latitude']}
                    onSubmit={createEquipment}
                    flex
                    placeholder="DD.DDDDDD°"
                    required
                  />
                  <Input
                    id="longitude"
                    label={t('equipment:longitude')}
                    value={equipmentStore.formValues.longitude}
                    type="number"
                    setValue={(value) => {
                      equipmentStore.updateFormValue('longitude', value);
                      setIsUserLocation(true);
                    }}
                    error={equipmentStore.formErrors['longitude']}
                    onSubmit={createEquipment}
                    flex
                    placeholder="DDD.DDDDDD°"
                    required
                  />
                </Box>
              </FormCardSection>
            </Box>
          </Box>
        </Form>
      </FormCard>
    </FormPage>
  );
});
